'use client';

import { Liveline } from '@outpoll/liveline';
import { useTheme } from 'next-themes';
import { useDayCloses } from '@/lib/api/futures/hooks';
import { SPARKLINE_COLOR } from './constants';

/** Liveline's "fit all data" window: the day spans the whole 64px. */
const FIT_ALL_DATA = 0;
/**
 * The canvas spills 12px past the 64×24 box (`-inset-3`) and pads it back, so
 * the line keeps the box while the live dot's pulse ring has room at any edge.
 */
const SPARKLINE_PADDING = { top: 12, right: 12, bottom: 12, left: 12 };

interface PairSparklineProps {
  pairId: number;
  /** Latest price — the line runs from the last hourly close to it. */
  last: number | undefined;
  changePct: number | undefined;
}

/** The pair's last 24 hours as a 64×24 line with a fading fill (Figma "Sparkline"). */
export function PairSparkline({ pairId, last, changePct }: PairSparklineProps) {
  const { resolvedTheme } = useTheme();
  const { data: closes } = useDayCloses(pairId);

  const lastClose = closes?.at(-1)?.value;
  const value = last ?? lastClose;
  const color = (changePct ?? 0) < 0 ? SPARKLINE_COLOR.down : SPARKLINE_COLOR.up;

  return (
    <div aria-hidden className="relative h-6 w-16 shrink-0">
      {closes?.length && value != null ? (
        <div className="absolute -inset-3">
          <Liveline
            data={closes}
            value={value}
            color={color}
            theme={resolvedTheme === 'light' ? 'light' : 'dark'}
            window={FIT_ALL_DATA}
            grid={false}
            axisX={false}
            axisY={false}
            badge={false}
            momentum={false}
            scrub={false}
            dot="compact"
            dashLine={false}
            fill
            exaggerate
            lineWidth={1}
            cursor="pointer"
            padding={SPARKLINE_PADDING}
            hideWindows
            hideSeriesToggle
          />
        </div>
      ) : null}
    </div>
  );
}
